import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toDashboardUiError } from '@/lib/dashboard-errors';

type CompletionState =
  | { kind: 'working' }
  | { kind: 'done'; label: string; email: string }
  | { kind: 'failed'; message: string };

/**
 * Finishes an OAuth connection in the connection window (audit S4): the public
 * callback forwards `code` and `state` in the fragment, and `oauth-complete`
 * checks under this session that the connection was started by this user.
 */
export const OAuthCompletePage = () => {
  const started = useRef(false);
  const [state, setState] = useState<CompletionState>({ kind: 'working' });

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const params = new URLSearchParams(window.location.hash.slice(1));
    // The code is single-use: never leave it in the address bar or history.
    window.history.replaceState(null, '', window.location.pathname);
    const code = params.get('code');
    const oauthState = params.get('state');
    if (!code || !oauthState) {
      setState({ kind: 'failed', message: 'Lien de connexion incomplet. Relancez la connexion depuis Domino.' });
      return;
    }
    void (async () => {
      const { data, error } = await supabase.functions.invoke('oauth-complete', { body: { code, state: oauthState } });
      if (error || !data?.success) {
        setState({ kind: 'failed', message: (await toDashboardUiError(error, 'La connexion n’a pas pu être terminée. Relancez-la depuis Domino.')).message });
        return;
      }
      setState({ kind: 'done', label: String(data.providerLabel ?? ''), email: String(data.email ?? '') });
      window.setTimeout(() => window.close(), 1500);
    })();
  }, []);

  return (
    <main className="flex min-h-screen items-center justify-center bg-domino-warm px-4">
      <div className="w-full max-w-md rounded-2xl border border-domino-cardBorder bg-domino-white p-6 text-center">
        {state.kind === 'working' && <p className="text-sm text-domino-inkSoft">Connexion…</p>}
        {state.kind === 'done' && (
          <>
            <h1 className="text-lg font-semibold text-domino-ink">{state.label} connecté</h1>
            {state.email && <p className="mt-1 text-sm text-domino-inkSoft">{state.email}</p>}
            <Link to="/connections" className="mt-4 inline-block text-sm text-domino-aubergine hover:underline">Connexions</Link>
          </>
        )}
        {state.kind === 'failed' && (
          <>
            <p role="alert" className="text-sm text-red-700">{state.message}</p>
            <Link to="/connections" className="mt-4 inline-block text-sm text-domino-aubergine hover:underline">Connexions</Link>
          </>
        )}
      </div>
    </main>
  );
};
